import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { getGoals, createGoal, updateGoal, deleteGoal, updateGoalProgress } from "../api/goalsClient";
import { formatCurrencyCompact, formatDate } from "../utils/formatters";

const TRACK_TYPES = [
    { value: "MANUAL", label: "Manual", description: "Update progress manually" },
    { value: "CASH", label: "Cash Balance", description: "Tracks your total cash" },
    { value: "INVESTMENTS", label: "Investments", description: "Tracks investment balance" },
    { value: "NET_WORTH", label: "Net Worth", description: "Tracks total net worth" },
];

const ICONS = [
    { value: "savings", label: "Savings", emoji: "💰" },
    { value: "house", label: "House", emoji: "🏠" },
    { value: "car", label: "Car", emoji: "🚗" },
    { value: "vacation", label: "Vacation", emoji: "✈️" },
    { value: "education", label: "Education", emoji: "🎓" },
    { value: "retirement", label: "Retirement", emoji: "🌴" },
    { value: "emergency", label: "Emergency Fund", emoji: "🛡️" },
    { value: "investment", label: "Investment", emoji: "📈" },
    { value: "debt", label: "Debt Payoff", emoji: "💳" },
    { value: "other", label: "Other", emoji: "🎯" },
];

function getIconEmoji(iconValue) {
    const icon = ICONS.find(i => i.value === iconValue);
    return icon ? icon.emoji : "🎯";
}

function GoalCard({ goal, onEdit, onDelete, onUpdateProgress }) {
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [addAmount, setAddAmount] = useState("");

    const progress = Math.min(goal.progressPercent || 0, 100);
    const isComplete = progress >= 100;
    const isManual = goal.trackType === "MANUAL";

    const handleProgressUpdate = async () => {
        const amountToAdd = parseFloat(addAmount) || 0;
        const newTotal = (goal.currentAmount || 0) + amountToAdd;
        await onUpdateProgress(goal.id, newTotal);
        setShowUpdateModal(false);
        setAddAmount("");
    };

    return (
        <div className="card p-5 relative">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-accent-50 rounded-lg flex items-center justify-center text-xl">
                        {getIconEmoji(goal.icon)}
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900">{goal.name}</h3>
                        <p className="text-xs text-gray-500">
                            {TRACK_TYPES.find(t => t.value === goal.trackType)?.label || goal.trackType}
                        </p>
                    </div>
                </div>
                <div className="flex gap-1">
                    <button
                        onClick={() => onEdit(goal)}
                        className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                        title="Edit goal"
                    >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                    </button>
                    <button
                        onClick={() => onDelete(goal)}
                        className="p-1.5 text-gray-400 hover:text-danger-500 hover:bg-gray-100 rounded transition-colors"
                        title="Delete goal"
                    >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Progress */}
            <div className="mb-3">
                <div className="flex justify-between items-baseline mb-2">
                    <span className="text-lg font-semibold text-gray-900 tracking-tight">
                        {formatCurrencyCompact(goal.currentAmount)}
                    </span>
                    <span className="text-sm text-gray-500">
                        of {formatCurrencyCompact(goal.targetAmount)}
                    </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all duration-500 ${
                            isComplete ? "bg-success-500" : "bg-accent-500"
                        }`}
                        style={{ width: `${progress}%` }}
                    />
                </div>
                <div className="flex justify-between items-center mt-2">
                    <span className={`text-sm font-medium ${isComplete ? "text-success-600" : "text-accent-600"}`}>
                        {progress.toFixed(0)}% complete
                    </span>
                    {goal.targetDate && (
                        <span className="text-xs text-gray-500">
                            Target: {formatDate(goal.targetDate)}
                        </span>
                    )}
                </div>
            </div>

            {/* Manual update button */}
            {isManual && (
                <button
                    onClick={() => {
                        setAddAmount("");
                        setShowUpdateModal(true);
                    }}
                    className="w-full mt-2 py-2 text-sm font-medium text-accent-600 hover:text-accent-700 border border-gray-200 hover:border-accent-300 rounded-lg transition-colors"
                >
                    Add Contribution
                </button>
            )}

            {/* Update Progress Modal */}
            {showUpdateModal && createPortal(
                <div className="fixed inset-0 bg-gray-900/40 z-50 flex items-center justify-center p-4" onClick={() => setShowUpdateModal(false)}>
                    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add contribution</h3>
                        <div className="mb-2 text-sm text-gray-600">
                            Current: {formatCurrencyCompact(goal.currentAmount)}
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm text-gray-600 mb-2">Amount to Add</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                                <input
                                    type="number"
                                    value={addAmount}
                                    onChange={(e) => setAddAmount(e.target.value)}
                                    className="input w-full pl-7"
                                    placeholder="500"
                                    step="0.01"
                                    autoFocus
                                />
                            </div>
                            {addAmount && (
                                <div className="mt-2 text-sm text-accent-600 font-medium">
                                    New total: {formatCurrencyCompact((goal.currentAmount || 0) + (parseFloat(addAmount) || 0))}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={handleProgressUpdate}
                                className="btn-primary flex-1"
                                disabled={!addAmount || parseFloat(addAmount) === 0}
                            >
                                Add
                            </button>
                            <button
                                onClick={() => setShowUpdateModal(false)}
                                className="btn-secondary flex-1"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

function GoalModal({ goal, onSave, onClose }) {
    const [formData, setFormData] = useState({
        name: goal?.name || "",
        targetAmount: goal?.targetAmount || "",
        currentAmount: goal?.currentAmount || 0,
        targetDate: goal?.targetDate || "",
        trackType: goal?.trackType || "MANUAL",
        icon: goal?.icon || "savings",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const isEdit = !!goal?.id;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);

        try {
            const payload = {
                ...formData,
                targetAmount: parseFloat(formData.targetAmount),
                currentAmount: parseFloat(formData.currentAmount) || 0,
                targetDate: formData.targetDate || null,
            };
            await onSave(payload, goal?.id);
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save goal");
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 bg-gray-900/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white border border-gray-200 rounded-xl shadow-lg w-full max-w-md max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                {/* Fixed header */}
                <div className="px-5 pt-4 pb-3 border-b border-gray-200 flex-shrink-0">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {isEdit ? "Edit goal" : "Create new goal"}
                    </h2>
                </div>

                {/* Scrollable body */}
                <div className="px-5 py-3 overflow-y-auto flex-1 min-h-0">
                    {error && (
                        <div className="mb-3 p-2 bg-danger-500/5 border border-danger-500/20 rounded-lg">
                            <p className="text-sm text-danger-600">{error}</p>
                        </div>
                    )}

                    <form id="goal-form" onSubmit={handleSubmit} className="space-y-3">
                        {/* Icon Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Icon</label>
                            <div className="flex flex-wrap gap-1.5">
                                {ICONS.map((icon) => (
                                    <button
                                        key={icon.value}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, icon: icon.value })}
                                        className={`w-9 h-9 rounded-lg text-base flex items-center justify-center transition-colors ${
                                            formData.icon === icon.value
                                                ? "bg-accent-50 border-2 border-accent-500"
                                                : "bg-gray-50 border-2 border-transparent hover:border-gray-200"
                                        }`}
                                        title={icon.label}
                                    >
                                        {icon.emoji}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Goal Name</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="input w-full"
                                placeholder="e.g., Emergency Fund"
                                required
                                maxLength={100}
                            />
                        </div>

                        {/* Track Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Track Using</label>
                            <select
                                value={formData.trackType}
                                onChange={(e) => setFormData({ ...formData, trackType: e.target.value })}
                                className="input w-full"
                            >
                                {TRACK_TYPES.map((type) => (
                                    <option key={type.value} value={type.value}>
                                        {type.label} - {type.description}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-0.5">
                                {formData.trackType === "MANUAL"
                                    ? "You'll update progress manually"
                                    : "Progress updates automatically based on your linked accounts"}
                            </p>
                        </div>

                        {/* Target Amount */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Target Amount</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                                <input
                                    type="number"
                                    value={formData.targetAmount}
                                    onChange={(e) => setFormData({ ...formData, targetAmount: e.target.value })}
                                    className="input w-full pl-7"
                                    placeholder="10000"
                                    required
                                    min="1"
                                    step="0.01"
                                />
                            </div>
                        </div>

                        {/* Current Amount (only for manual) */}
                        {formData.trackType === "MANUAL" && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Current Amount</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                                    <input
                                        type="number"
                                        value={formData.currentAmount}
                                        onChange={(e) => setFormData({ ...formData, currentAmount: e.target.value })}
                                        className="input w-full pl-7"
                                        placeholder="0"
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Target Date */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Target Date (optional)</label>
                            <input
                                type="date"
                                value={formData.targetDate}
                                onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                                className="input w-full"
                            />
                        </div>
                    </form>
                </div>

                {/* Fixed footer */}
                <div className="px-5 py-3 border-t border-gray-200 flex gap-3 flex-shrink-0">
                    <button
                        type="submit"
                        form="goal-form"
                        disabled={saving}
                        className="btn-primary flex-1"
                    >
                        {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Goal"}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="btn-secondary flex-1"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

function DeleteConfirmModal({ goal, onConfirm, onClose }) {
    const [deleting, setDeleting] = useState(false);

    const handleDelete = async () => {
        setDeleting(true);
        await onConfirm(goal.id);
        onClose();
    };

    return createPortal(
        <div className="fixed inset-0 bg-gray-900/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
                <div className="w-12 h-12 bg-danger-500/5 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-danger-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Delete goal</h3>
                <p className="text-gray-600 text-center mb-6">
                    Are you sure you want to delete "{goal.name}"? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                    <button
                        onClick={handleDelete}
                        disabled={deleting}
                        className="flex-1 py-2.5 px-4 bg-danger-500 hover:bg-danger-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                        {deleting ? "Deleting..." : "Delete"}
                    </button>
                    <button
                        onClick={onClose}
                        disabled={deleting}
                        className="btn-secondary flex-1"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default function GoalsPage() {
    const [goals, setGoals] = useState([]);
    const [status, setStatus] = useState("loading");
    const [error, setError] = useState("");

    const [showModal, setShowModal] = useState(false);
    const [editingGoal, setEditingGoal] = useState(null);
    const [deletingGoal, setDeletingGoal] = useState(null);
    const abortControllerRef = useRef(null);

    const fetchGoals = useCallback(async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        setStatus("loading");
        setError("");
        try {
            const data = await getGoals(signal);
            setGoals(data);
            setStatus("idle");
        } catch (err) {
            if (err.name === 'AbortError' || err.name === 'CanceledError') {
                return;
            }
            setStatus("error");
            setError(err.response?.data?.message || "Failed to load goals");
        }
    }, []);

    useEffect(() => {
        fetchGoals();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchGoals]);

    const handleSave = async (formData, goalId) => {
        if (goalId) {
            await updateGoal(goalId, formData);
        } else {
            await createGoal(formData);
        }
        await fetchGoals();
    };

    const handleDelete = async (goalId) => {
        await deleteGoal(goalId);
        await fetchGoals();
    };

    const handleUpdateProgress = async (goalId, amount) => {
        await updateGoalProgress(goalId, amount);
        await fetchGoals();
    };

    const openEditModal = (goal) => {
        setEditingGoal(goal);
        setShowModal(true);
    };

    const openCreateModal = () => {
        setEditingGoal(null);
        setShowModal(true);
    };

    if (status === "loading") {
        return (
            <div className="max-w-4xl mx-auto">
                <div className="card p-6">
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin w-6 h-6 border-2 border-gray-200 border-t-gray-700 rounded-full" />
                        <span className="ml-3 text-gray-500">Loading goals...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (status === "error") {
        return (
            <div className="max-w-4xl mx-auto">
                <div className="card p-6 text-center">
                    <div className="w-12 h-12 bg-danger-500/5 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-6 h-6 text-danger-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <p className="text-danger-500 mb-4">{error}</p>
                    <button onClick={fetchGoals} className="btn-primary">
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">Goals</h1>
                    <p className="text-gray-500 mt-1 text-sm sm:text-base">Track your financial milestones</p>
                </div>
                <button onClick={openCreateModal} className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    New Goal
                </button>
            </div>

            {/* Goals Grid */}
            {goals.length === 0 ? (
                <div className="card p-8 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">
                        🎯
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No goals yet</h3>
                    <p className="text-gray-600 mb-6 max-w-sm mx-auto">
                        Set your first financial goal and start tracking your progress toward financial freedom.
                    </p>
                    <button onClick={openCreateModal} className="btn-primary">
                        Create Your First Goal
                    </button>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2">
                    {goals.map((goal) => (
                        <GoalCard
                            key={goal.id}
                            goal={goal}
                            onEdit={openEditModal}
                            onDelete={setDeletingGoal}
                            onUpdateProgress={handleUpdateProgress}
                        />
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <GoalModal
                    goal={editingGoal}
                    onSave={handleSave}
                    onClose={() => {
                        setShowModal(false);
                        setEditingGoal(null);
                    }}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deletingGoal && (
                <DeleteConfirmModal
                    goal={deletingGoal}
                    onConfirm={handleDelete}
                    onClose={() => setDeletingGoal(null)}
                />
            )}
        </div>
    );
}
